import { useState } from "react";
import { Check, Loader2, Star } from "lucide-react";
import { cn } from "@/lib/cn";
import { useTranslation } from "@/i18n/I18nProvider";
import { submitFeedback } from "@/lib/visits";
import { Button } from "@/components/ui/Button";

/**
 * Star-first feedback form, reused by the Feedbacks tab and the delayed rating
 * prompt. Progressive disclosure: only the star row shows until a star is
 * tapped, then the (optional) "approx RON per person" + message fields reveal.
 * The rating is the ONLY required field — a stars-only review is valid.
 */

export function StarRating({
  value,
  onChange,
  size = "md",
}: {
  value: number;
  onChange: (v: number) => void;
  size?: "md" | "lg";
}) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  const dim = size === "lg" ? "h-9 w-9" : "h-7 w-7";
  return (
    <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          aria-label={t("FEEDBACK_STAR", { n })}
          aria-pressed={value === n}
          onMouseEnter={() => setHover(n)}
          onClick={() => onChange(n)}
          className="rounded-md p-0.5 transition hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60"
        >
          <Star
            className={cn(
              dim,
              n <= shown ? "fill-amber-300 text-amber-300" : "fill-transparent text-zinc-600",
            )}
            aria-hidden
          />
        </button>
      ))}
    </div>
  );
}

export function FeedbackForm({
  visitId,
  initialRating = null,
  onSubmitted,
  onSkip,
}: {
  visitId: number;
  initialRating?: number | null;
  onSubmitted: () => void;
  /** When present, a Skip control is shown (used by the delayed prompt). */
  onSkip?: () => void;
}) {
  const { t } = useTranslation();
  const [rating, setRating] = useState(initialRating ?? 0);
  const [spend, setSpend] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (rating < 1) return;
    setError(false);
    setSubmitting(true);
    const spendNum = spend.trim() === "" ? null : Number(spend);
    const ok = await submitFeedback({
      visitId,
      rating,
      spend: spendNum != null && Number.isFinite(spendNum) && spendNum >= 0 ? spendNum : null,
      feedback: message.trim() || null,
    });
    setSubmitting(false);
    if (ok) onSubmitted();
    else setError(true);
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
      <div className="space-y-1.5">
        <span className="text-3xs font-medium uppercase tracking-wider text-dim light:text-zinc-600">
          {t("FEEDBACK_RATING_LABEL")}
        </span>
        <StarRating value={rating} onChange={setRating} size="lg" />
        {rating < 1 && <p className="px-0.5 text-xs text-dim light:text-zinc-600">{t("FEEDBACK_STARS_HINT")}</p>}
      </div>

      {/* Progressive disclosure — spend + message appear once a star is picked. */}
      {rating >= 1 && (
        <div className="space-y-4">
          <label className="block space-y-1.5">
            <span className="text-3xs font-medium uppercase tracking-wider text-dim light:text-zinc-600">
              {t("FEEDBACK_SPEND_LABEL")}
            </span>
            <input
              type="number"
              inputMode="decimal"
              min={0}
              step="1"
              value={spend}
              onChange={(e) => setSpend(e.target.value)}
              placeholder={t("FEEDBACK_SPEND_PLACEHOLDER")}
              className="w-full rounded-xl border border-white/[0.12] light:border-black/10 bg-zinc-900/60 light:bg-white/70 px-3.5 py-2.5 text-base text-zinc-100 light:text-zinc-900 outline-none transition focus:border-zinc-500 light:focus:border-black/40 sm:text-sm"
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-3xs font-medium uppercase tracking-wider text-dim light:text-zinc-600">
              {t("FEEDBACK_MESSAGE_LABEL")}
            </span>
            <textarea
              value={message}
              maxLength={2000}
              rows={3}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t("FEEDBACK_MESSAGE_PLACEHOLDER")}
              className="w-full resize-none rounded-xl border border-white/[0.12] light:border-black/10 bg-zinc-900/60 light:bg-white/70 px-3.5 py-2.5 text-base text-zinc-100 light:text-zinc-900 outline-none transition focus:border-zinc-500 light:focus:border-black/40 sm:text-sm"
            />
          </label>
        </div>
      )}

      {error && (
        <p role="alert" className="rounded-lg border border-rose-900/40 bg-rose-950/30 px-3 py-2 text-xs text-rose-300">
          {t("FEEDBACK_ERROR")}
        </p>
      )}

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={submitting || rating < 1}>
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Check className="h-4 w-4" aria-hidden />}
          {t("FEEDBACK_SUBMIT")}
        </Button>
        {onSkip && (
          <button
            type="button"
            onClick={onSkip}
            className="text-sm font-medium text-zinc-400 light:text-zinc-600 transition hover:text-zinc-100 light:hover:text-zinc-900"
          >
            {t("FEEDBACK_SKIP")}
          </button>
        )}
      </div>
    </form>
  );
}
